/**
 * Membership plan page (DNKADM-0496) — one `v2_membership_plan`, routed at
 * `/membership-credits/plans/:planId` (the Plans tab stays selected).
 *
 * **Read-only cards, one Edit each.** Six summary cards, top to bottom: Basics; What it
 * includes; Access hours; Booking limits; Who can buy and where; Taxes and payment. Each card
 * shows its values as text. Its one Edit opens a right-side {@link EditDrawer} (the service
 * page's, DNKADM-0493), and nothing outside a drawer saves. The drawers reuse the existing
 * editors, so the calls, bodies and validation are the ones the old Edit plan dialog made:
 *  - Basics, What it includes, Who can buy and where: {@link PlanFields} (the
 *    {@link PlanDialog} form, split by card) PUTs the same whole-plan body, with only that
 *    card's fields changed; an unchanged drawer sends nothing. What it includes also hosts the
 *    {@link PlanCreditGrantsEditor}, live against the plan id as it was in the dialog, with its
 *    reduce-credits confirm for current members.
 *  - Access hours: {@link PlanAccessHoursSection}, unchanged (its own Save and its Remove
 *    confirm), so that drawer's footer is Done.
 *  - Booking limits: this plan's daily caps, each opened in the existing {@link CapDialog}
 *    (same create/update bodies). Footer Done.
 *  - Taxes and payment has no Edit: no plan screen ever wrote `taxable`, and "Pause membership
 *    credits when a payment fails" is a setting for every plan, edited in Plan settings.
 *
 * **Header.** Name, the status chip (Draft / Turned off / Active, the service page's chip and
 * words) and a More menu with the plan's existing actions: Duplicate (the copy starts as a
 * Draft, and the page opens it), Turn on (publish a Draft, reactivate a turned-off plan) /
 * Turn off (deactivate). A Draft keeps the dialog's note that publishing a priced plan makes it
 * billable.
 *
 * The plan comes from the same list read the Plans tab makes (`status=all`), so the page shows
 * the same `impact` the dialog used for its member notices.
 */
import { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Link as MuiLink,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { Icon } from '@/theme';
import { IconAction } from '@/components/IconAction';
import { ScreenError } from '@/components/ScreenError';
import { useToast } from '@/components/useToast';
import type { ShowToast } from '@/components/toastContext';
import { useResource, type Resource } from '@/hooks/useResource';
import { useTenantCurrency } from '@/hooks/useTenantCurrency';
import { serverMessage } from '@/api/serverMessage';
import {
  copyMembershipPlan,
  deactivateMembershipPlan,
  listMembershipPlans,
  publishMembershipPlan,
  reactivateMembershipPlan,
  type MembershipPlan,
} from '@/api/membershipPlans';
import { listPlanCredits, type PlanCredit } from '@/api/membershipTierCredits';
import { getPlanAccessHours } from '@/api/membershipPlanAccessHours';
import type { WeeklyHoursDay } from '@/api/operatingHours';
import { listDailyCaps, type DailyCap } from '@/api/dailyCaps';
import { listCreditBuckets } from '@/api/creditBuckets';
import { listLocations } from '@/api/locations';
import { getMembershipSettings } from '@/api/membershipSettings';
import { clock12 } from '@/utils/clockTime';
import { formatCreditAmount, formatHours } from '@/utils/v2Format';
import { EditDrawer } from '@/features/services/EditDrawer';
import { SummaryCard, SummaryRow } from '@/features/services/SummaryCard';
import { ServiceStatusChip } from '@/features/services/ServiceStatusChip';
import { CapDialog } from '@/features/daily-caps/CapDialog';
import { PlanFields, type PlanFormHandle, type PlanFormSection } from './PlanDialog';
import { PlanAccessHoursSection } from './PlanAccessHoursSection';
import { planAudienceLabel, planName, planPriceLabel, planTermLabel } from './plansListView';

type CardDrawer = PlanFormSection | 'hours' | 'limits';

const DRAWER_TITLES: Record<CardDrawer, string> = {
  basics: 'Basics',
  includes: 'What it includes',
  who: 'Who can buy and where',
  hours: 'Access hours',
  limits: 'Booking limits',
};

const PLANS_PATH = '/membership-credits/plans';

/** Sun–Sat short labels indexed by `dayOfWeek` (0=Sunday … 6=Saturday). */
const SHORT_DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** "6:00 AM" from an `HH:mm[:ss]` wall clock, or the raw text when it can't be split. */
function clockLabel(text: string): string {
  const match = /^(\d{1,2}):(\d{2})/.exec(text);
  return match ? clock12(Number(match[1]), Number(match[2])) : text;
}

/** One day's access-hours line: "6:00 AM – 9:00 PM" (several shifts joined), or "Closed". */
function dayHours(day: WeeklyHoursDay): string {
  if (!day.isAvailable || day.shifts.length === 0) return 'Closed';
  return day.shifts.map((s) => `${clockLabel(s.startTime)} – ${clockLabel(s.endTime)}`).join(', ');
}

/** A read the card depends on, shown as "—" while loading and "Couldn’t load" on failure. */
function pending(resource: Resource<unknown>): string | null {
  if (resource.data !== null) return null;
  return resource.status === 'error' ? 'Couldn’t load' : '—';
}

export function PlanDetail() {
  const { planId = '' } = useParams<{ planId: string }>();
  const showToast = useToast();
  const navigate = useNavigate();
  const { currency } = useTenantCurrency();

  // The plan, from the same list read the Plans tab makes (every status, with `impact`).
  const plans = useResource(
    useCallback(() => listMembershipPlans({ status: 'all' }), []),
    [],
  );
  const plan = useMemo(
    () => (plans.data ?? []).find((p) => p.id === planId) ?? null,
    [plans.data, planId],
  );

  // The same reads the dialog's editors made, now made for the cards.
  const credits = useResource(
    useCallback(() => listPlanCredits(planId), [planId]),
    [planId],
  );
  const accessHours = useResource(
    useCallback(() => getPlanAccessHours(planId), [planId]),
    [planId],
  );
  const caps = useResource(
    useCallback(() => listDailyCaps(), []),
    [],
  );
  const buckets = useResource(
    useCallback(() => listCreditBuckets({ status: 'all' }), []),
    [],
  );
  const locations = useResource(
    useCallback(() => listLocations({ status: 'all' }), []),
    [],
  );
  const settings = useResource(
    useCallback(() => getMembershipSettings(), []),
    [],
  );

  const bucketNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const b of buckets.data ?? []) map.set(b.id, b.name.length > 0 ? b.name : b.id);
    return map;
  }, [buckets.data]);
  const locationNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const l of locations.data ?? []) map.set(l.id, l.name.length > 0 ? l.name : l.id);
    return map;
  }, [locations.data]);
  const planCaps = useMemo(
    () => (caps.data ?? []).filter((c) => c.membershipPlanId === planId),
    [caps.data, planId],
  );

  // ---- Card drawers ----
  const [drawer, setDrawer] = useState<CardDrawer | null>(null);
  const formRef = useRef<PlanFormHandle>(null);

  const refetchPlans = plans.refetch;
  const refetchCredits = credits.refetch;
  const refetchHours = accessHours.refetch;
  const refetchCaps = caps.refetch;
  const refreshPage = useCallback(() => {
    refetchPlans();
    refetchCredits();
    refetchHours();
    refetchCaps();
  }, [refetchPlans, refetchCredits, refetchHours, refetchCaps]);

  const closeDrawer = () => {
    setDrawer(null);
    // The page re-reads on close (the live editors may have written while it was open).
    refreshPage();
  };

  const saveForm = async (): Promise<boolean> => {
    const handle = formRef.current;
    if (handle && !(await handle.save())) return false;
    showToast('Changes saved', 'success');
    return true;
  };

  // ---- Header actions (More menu) ----
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  async function runAction(action: () => Promise<unknown>, done: string, failed: string) {
    setMenuAnchor(null);
    try {
      await action();
      refetchPlans();
      showToast(done, 'success');
    } catch (err) {
      showToast(serverMessage(err) ?? failed, 'error');
    }
  }

  async function handleDuplicate(p: MembershipPlan) {
    setMenuAnchor(null);
    try {
      const copy = await copyMembershipPlan(p.id);
      showToast('Plan duplicated. The copy starts as a Draft.', 'success');
      if (copy) navigate(`${PLANS_PATH}/${copy.id}`);
    } catch (err) {
      showToast(serverMessage(err) ?? 'Couldn’t duplicate the plan.', 'error');
    }
  }

  const notFound = plans.status === 'ready' && plan === null;

  return (
    <Box>
      <MuiLink
        component={RouterLink}
        to={PLANS_PATH}
        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: 13, mb: 2 }}
      >
        <Icon name="arrow_back" sx={{ fontSize: 18 }} />
        All plans
      </MuiLink>

      {plans.status === 'loading' && !plan && (
        <Skeleton
          variant="rounded"
          height={120}
          role="status"
          aria-label="Loading plan"
          sx={{ borderRadius: 3, maxWidth: 560 }}
        />
      )}

      {plans.status === 'error' && !plan && (
        <ScreenError
          error={plans.error}
          what="the plan"
          onRetry={plans.refetch}
          sx={{ maxWidth: 560 }}
        />
      )}

      {notFound && (
        <Alert severity="warning" sx={{ maxWidth: 560 }}>
          That plan wasn’t found.{' '}
          <MuiLink component={RouterLink} to={PLANS_PATH}>
            Back to plans
          </MuiLink>
          .
        </Alert>
      )}

      {/* While the page re-reads after a drawer closes, the last values stay on screen. */}
      {plan && (
        <>
          <Stack direction="row" sx={{ alignItems: 'center', gap: 1.5, mb: 3 }}>
            <Typography variant="h6" component="h1">
              {planName(plan)}
            </Typography>
            <ServiceStatusChip status={plan.status} />
            <Box sx={{ flex: 1 }} />
            <IconAction label="More actions" onClick={(e) => setMenuAnchor(e.currentTarget)}>
              <Icon name="more_vert" />
            </IconAction>
            <Menu anchorEl={menuAnchor} open={menuAnchor !== null} onClose={() => setMenuAnchor(null)}>
              <MenuItem onClick={() => void handleDuplicate(plan)}>Duplicate</MenuItem>
              {plan.status === 'ACTIVE' ? (
                <MenuItem
                  onClick={() =>
                    void runAction(
                      () => deactivateMembershipPlan(plan.id),
                      'Plan turned off',
                      'Couldn’t turn the plan off.',
                    )
                  }
                >
                  Turn off
                </MenuItem>
              ) : (
                <MenuItem
                  onClick={() =>
                    void runAction(
                      // A Draft goes live by publishing it; a turned-off plan comes back by
                      // reactivating it (the list's Publish / Reactivate).
                      () =>
                        plan.status === 'DRAFT'
                          ? publishMembershipPlan(plan.id)
                          : reactivateMembershipPlan(plan.id),
                      'Plan turned on',
                      'Couldn’t turn the plan on.',
                    )
                  }
                >
                  Turn on
                </MenuItem>
              )}
            </Menu>
          </Stack>

          {/* The Edit plan dialog's Draft note, kept (DNKAPI-0265: publishing a priced plan
              registers it for billing). */}
          {plan.status === 'DRAFT' && (
            <Alert severity="info" role="status" sx={{ mb: 3, maxWidth: 880 }}>
              Draft — not yet live.
              {plan.price > 0 ? ' Publishing this plan also makes it billable.' : ''}
            </Alert>
          )}

          <Stack spacing={2} sx={{ maxWidth: 880, mb: 4 }}>
            <SummaryCard title="Basics" onEdit={() => setDrawer('basics')}>
              <SummaryRow label="Name" value={planName(plan)} />
              <SummaryRow label="Description" value={plan.description ?? 'None'} />
              <SummaryRow label="Price" value={planPriceLabel(plan, currency)} />
              <SummaryRow label="Billing interval" value={planTermLabel(plan)} last />
            </SummaryCard>

            <IncludesCard
              plan={plan}
              credits={credits}
              bucketNameById={bucketNameById}
              onEdit={() => setDrawer('includes')}
            />

            <AccessHoursCard accessHours={accessHours} onEdit={() => setDrawer('hours')} />

            <BookingLimitsCard
              caps={caps}
              planCaps={planCaps}
              bucketNameById={bucketNameById}
              onEdit={() => setDrawer('limits')}
            />

            <SummaryCard title="Who can buy and where" onEdit={() => setDrawer('who')}>
              <SummaryRow
                label="Available at"
                value={
                  plan.locationIds.length === 0
                    ? 'All locations'
                    : (pending(locations) ??
                      plan.locationIds.map((id) => locationNameById.get(id) ?? id).join(', '))
                }
              />
              <SummaryRow
                label="Who can buy"
                value={planAudienceLabel(plan)}
                hint={
                  plan.staffOnly
                    ? 'Members won’t see this plan. The front desk can still sell it.'
                    : 'Members can buy this plan themselves, and the front desk can sell it.'
                }
                last
              />
            </SummaryCard>

            <SummaryCard title="Taxes and payment" onEdit={() => {}} noEdit>
              <SummaryRow label="Taxable" value={plan.taxable ? 'Yes' : 'No'} />
              <SummaryRow
                label="Pause membership credits when a payment fails"
                hint="Applies to every plan."
                value={
                  <Stack sx={{ alignItems: 'flex-end' }}>
                    <Typography variant="body2">
                      {pending(settings) ??
                        (settings.data?.blockMembershipCreditsOnPaymentFailure ? 'On' : 'Off')}
                    </Typography>
                    <MuiLink component={RouterLink} to="/membership-credits/settings" variant="body2">
                      Change in Plan settings
                    </MuiLink>
                  </Stack>
                }
                last
              />
            </SummaryCard>
          </Stack>

          <EditDrawer
            open={drawer !== null}
            title={drawer ? DRAWER_TITLES[drawer] : ''}
            onClose={closeDrawer}
            doneOnly={drawer === 'hours' || drawer === 'limits'}
            {...(drawer === 'hours' || drawer === 'limits' ? {} : { onSave: saveForm })}
          >
            {drawer === 'basics' || drawer === 'includes' || drawer === 'who' ? (
              <PlanFields
                key={drawer}
                plan={plan}
                sections={[drawer]}
                handleRef={formRef}
                currency={currency}
                showToast={showToast}
              />
            ) : drawer === 'hours' ? (
              <PlanAccessHoursSection planId={plan.id} showToast={showToast} />
            ) : drawer === 'limits' ? (
              <BookingLimitsEditor
                planCaps={planCaps}
                allCaps={caps.data ?? []}
                capsFailed={caps.status === 'error' && caps.data === null}
                onRetry={refetchCaps}
                bucketNameById={bucketNameById}
                onSaved={refetchCaps}
                showToast={showToast}
              />
            ) : null}
          </EditDrawer>
        </>
      )}
    </Box>
  );
}

// ---- Cards with their own reads ----

function IncludesCard({
  plan,
  credits,
  bucketNameById,
  onEdit,
}: {
  plan: MembershipPlan;
  credits: Resource<PlanCredit[]>;
  bucketNameById: Map<string, string>;
  onEdit: () => void;
}) {
  const grantLine = (rows: PlanCredit[]) =>
    rows.length === 0
      ? 'None'
      : rows
          .map((c) => formatCreditAmount(c.amount, bucketNameById.get(c.creditBucketId) ?? null))
          .join(', ');
  const waiting = pending(credits);
  const all = credits.data ?? [];
  return (
    <SummaryCard title="What it includes" onEdit={onEdit}>
      <SummaryRow
        label="Credits each billing period"
        value={waiting ?? grantLine(all.filter((c) => c.recurrence === 'recurring'))}
      />
      <SummaryRow
        label="First purchase extras"
        value={waiting ?? grantLine(all.filter((c) => c.recurrence !== 'recurring'))}
      />
      <SummaryRow
        label="Credit refills"
        value={plan.creditsAdditive ? 'Additive' : 'Replace'}
        hint={
          plan.creditsAdditive
            ? 'Leftover credits carry over until they expire.'
            : 'Leftover credits don’t carry over. Each renewal starts fresh.'
        }
        last
      />
    </SummaryCard>
  );
}

function AccessHoursCard({
  accessHours,
  onEdit,
}: {
  accessHours: Resource<{ days: WeeklyHoursDay[] | null }>;
  onEdit: () => void;
}) {
  const waiting = pending(accessHours);
  if (waiting) {
    return (
      <SummaryCard title="Access hours" onEdit={onEdit}>
        <SummaryRow label="When members can book" value={waiting} last />
      </SummaryCard>
    );
  }
  const days = accessHours.data?.days ?? null;
  if (days === null) {
    return (
      <SummaryCard
        title="Access hours"
        onEdit={onEdit}
        empty="Not set. Members on this plan get full facility hours."
      />
    );
  }
  return (
    <SummaryCard title="Access hours" onEdit={onEdit}>
      {days.map((day, i) => (
        <SummaryRow
          key={day.dayOfWeek}
          label={SHORT_DAYS[day.dayOfWeek] ?? `Day ${day.dayOfWeek}`}
          value={dayHours(day)}
          last={i === days.length - 1}
        />
      ))}
    </SummaryCard>
  );
}

/** "1.5 hours a day" for one cap; which credits it counts, as the row label. */
function capRow(cap: DailyCap, bucketNameById: Map<string, string>): { label: string; value: string } {
  const label =
    cap.creditBucketId === null
      ? 'All bookings'
      : (bucketNameById.get(cap.creditBucketId) ?? cap.creditBucketId);
  return { label, value: `${formatHours(cap.capHours)} hours a day` };
}

function BookingLimitsCard({
  caps,
  planCaps,
  bucketNameById,
  onEdit,
}: {
  caps: Resource<DailyCap[]>;
  planCaps: DailyCap[];
  bucketNameById: Map<string, string>;
  onEdit: () => void;
}) {
  const waiting = pending(caps);
  if (waiting) {
    return (
      <SummaryCard title="Booking limits" onEdit={onEdit}>
        <SummaryRow label="Daily limit" value={waiting} last />
      </SummaryCard>
    );
  }
  if (planCaps.length === 0) {
    return <SummaryCard title="Booking limits" onEdit={onEdit} empty="No daily limit for this plan" />;
  }
  return (
    <SummaryCard title="Booking limits" onEdit={onEdit}>
      {planCaps.map((cap, i) => {
        const row = capRow(cap, bucketNameById);
        return (
          <SummaryRow
            key={cap.id}
            label={row.label}
            value={row.value}
            last={i === planCaps.length - 1}
          />
        );
      })}
    </SummaryCard>
  );
}

/**
 * The Booking limits drawer: this plan's daily caps, each opened in the existing
 * {@link CapDialog} (the Daily caps tab's). The dialog's update body and its duplicate warning
 * (over every cap) are unchanged. New limits are added on the Daily caps tab: the dialog's
 * add mode starts at "All bookings", which from a plan page would read as this plan only.
 */
function BookingLimitsEditor({
  planCaps,
  allCaps,
  capsFailed,
  onRetry,
  bucketNameById,
  onSaved,
  showToast,
}: {
  planCaps: DailyCap[];
  allCaps: readonly DailyCap[];
  capsFailed: boolean;
  onRetry: () => void;
  bucketNameById: Map<string, string>;
  onSaved: () => void;
  showToast: ShowToast;
}) {
  const [dialog, setDialog] = useState<{ cap: DailyCap | null } | null>(null);
  return (
    <Box>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
        The most hours a member on this plan can book in one day. Add a limit, or set one for
        every plan, on the{' '}
        <MuiLink component={RouterLink} to="/membership-credits/daily-caps">
          Daily caps tab
        </MuiLink>
        .
      </Typography>
      {capsFailed ? (
        <Alert
          severity="error"
          sx={{ alignItems: 'center' }}
          action={
            <Button color="inherit" size="small" onClick={onRetry}>
              Retry
            </Button>
          }
        >
          Couldn’t load the daily limits. Please try again.
        </Alert>
      ) : (
        <>
          {planCaps.length === 0 && (
            <Typography variant="body2">No daily limit for this plan.</Typography>
          )}
          {planCaps.map((cap) => {
            const row = capRow(cap, bucketNameById);
            return (
              <Stack
                key={cap.id}
                direction="row"
                sx={{ alignItems: 'center', justifyContent: 'space-between', py: 0.75 }}
              >
                <Typography variant="body2">
                  {row.label}: {row.value}
                </Typography>
                <IconAction
                  size="small"
                  label={`Edit limit ${row.label}`}
                  onClick={() => setDialog({ cap })}
                >
                  <Icon name="edit" />
                </IconAction>
              </Stack>
            );
          })}
        </>
      )}
      <CapDialog
        open={dialog !== null}
        cap={dialog?.cap ?? null}
        caps={allCaps}
        onClose={() => setDialog(null)}
        onSaved={() => {
          setDialog(null);
          onSaved();
        }}
        showToast={showToast}
      />
    </Box>
  );
}
